// src/TestPanel.js
// Internal test panel — accessible from the home screen.
// Lets you walk through the entire agreement flow without real emails.

import { useState, useEffect } from "react";

const STATUS_COLORS = {
  pending_review:  { bg: "#FFF9C4", text: "#795548", dot: "#F9A825" },
  approved:        { bg: "#E8F5E9", text: "#2e7d32", dot: "#2e7d32" },
  sent:            { bg: "#E3F2FD", text: "#1565C0", dot: "#1565C0" },
  client_commented:{ bg: "#FFF3E0", text: "#b45309", dot: "#f59e0b" },
  client_signed:   { bg: "#EDE7F6", text: "#4527a0", dot: "#7c3aed" },
  signed:          { bg: "#E1F5EE", text: "#1D9E75", dot: "#1D9E75" },
};

const STATUS_LABELS = {
  pending_review:  "Pending Approval",
  approved:        "Approved",
  sent:            "Sent to Client",
  client_commented:"Client Has Comments",
  client_signed:   "Client Signed",
  signed:          "Fully Signed",
};

function Badge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#f4f5f7", text: "#666", dot: "#999" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: c.bg, color: c.text, fontSize: 11, fontWeight: 600,
      padding: "3px 9px", borderRadius: 20,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot }} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function Section({ title, children, accent = "#1a3a6e" }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 10, border: "0.5px solid #e5e7eb",
      marginBottom: 12, overflow: "hidden",
    }}>
      <div style={{
        background: accent, color: "#fff", padding: "8px 16px",
        fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
      }}>
        {title}
      </div>
      <div style={{ padding: "14px 16px" }}>{children}</div>
    </div>
  );
}

function Btn({ children, onClick, disabled, color = "#1a3a6e", outline = false }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: outline ? "#fff" : color,
      color: outline ? color : "#fff",
      border: `1.5px solid ${color}`,
      borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
    }}>
      {children}
    </button>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: copied ? "#1D9E75" : "#999", padding: "0 4px" }}>
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function AmendmentHistory({ draft }) {
  const amendments = draft.amendments || [];
  if (!amendments.length) {
    return <p style={{ color: "#aaa", fontSize: 12, margin: 0 }}>No amendments yet.</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {amendments.map((a, i) => (
        <div key={i} style={{
          borderLeft: "3px solid #e07b3f", borderRadius: "0 6px 6px 0",
          background: "#fff8f3", padding: "8px 12px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#e07b3f", background: "#ffe8d6", padding: "1px 7px", borderRadius: 10 }}>
              v{i + 2}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>{a.bossName}</span>
            <span style={{ fontSize: 11, color: "#aaa", marginLeft: "auto" }}>
              {new Date(a.at).toLocaleString()}
            </span>
          </div>
          <div style={{ fontSize: 13, color: "#555" }}>{a.note || "(no note)"}</div>
        </div>
      ))}
    </div>
  );
}

function BossActions({ draft, bossNum, onRefresh }) {
  const bossId   = `boss${bossNum}`;
  const approved = !!(draft.approvals?.[bossId]);
  const v        = draft.reviewVersion || 1;

  const [loading,          setLoading]          = useState(false);
  const [amendOpen,        setAmendOpen]        = useState(false);
  const [amendNote,        setAmendNote]        = useState("");
  const [changesOpen,      setChangesOpen]      = useState(false);
  const [changesFeedback,  setChangesFeedback]  = useState("");

  const approve = async () => {
    setLoading(true);
    try {
      await fetch(`/api/draft/${draft.token}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bossId, bossName: `Boss ${bossNum} (test)` }),
      });
      onRefresh();
    } catch { alert("Failed"); }
    finally { setLoading(false); }
  };

  const submitAmend = async () => {
    if (!amendNote.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/draft/${draft.token}/amend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bossId, bossName: `Boss ${bossNum} (test)`, note: amendNote }),
      });
      if (r.ok) { setAmendOpen(false); setAmendNote(""); onRefresh(); }
      else { const d = await r.json(); alert(d.error || "Failed"); }
    } catch { alert("Failed"); }
    finally { setLoading(false); }
  };

  const submitChanges = async () => {
    if (!changesFeedback.trim()) return;
    setLoading(true);
    try {
      await fetch(`/api/draft/${draft.token}/changes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bossId, bossName: `Boss ${bossNum} (test)`, feedback: changesFeedback }),
      });
      setChangesOpen(false); setChangesFeedback(""); onRefresh();
    } catch { alert("Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        {approved ? (
          <span style={{ fontSize: 12, fontWeight: 600, color: "#1D9E75" }}>✓ Approved</span>
        ) : (
          <>
            <Btn onClick={approve} disabled={loading} color="#1D9E75">
              {loading ? "…" : `✓ Approve as Boss ${bossNum}`}
            </Btn>
            <Btn
              onClick={() => { setAmendOpen(!amendOpen); setChangesOpen(false); }}
              disabled={loading} outline color="#e07b3f"
            >
              ✎ Suggest Amendments
            </Btn>
            <Btn
              onClick={() => { setChangesOpen(!changesOpen); setAmendOpen(false); }}
              disabled={loading} outline color="#993C1D"
            >
              Request Changes
            </Btn>
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <a
          href={`/review/${draft.token}?boss=${bossId}&v=${v}`}
          target="_blank" rel="noreferrer"
          style={{ fontSize: 12, color: "#1a3a6e", textDecoration: "underline" }}
        >
          Open review page (v{v}) ↗
        </a>
        {v > 1 && (
          <a
            href={`/review/${draft.token}?boss=${bossId}&v=${v - 1}`}
            target="_blank" rel="noreferrer"
            style={{ fontSize: 12, color: "#e07b3f", textDecoration: "underline" }}
            title="Simulates clicking an old email link — should trigger the version mismatch banner"
          >
            Simulate stale link (v{v - 1}) ↗
          </a>
        )}
      </div>

      {amendOpen && (
        <div style={{ background: "#fff8f3", border: "1px solid #e07b3f", borderRadius: 8, padding: 12, marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#e07b3f", marginBottom: 6 }}>
            Amendment note (required) — requeues both bosses:
          </div>
          <textarea
            value={amendNote}
            onChange={e => setAmendNote(e.target.value)}
            placeholder={`e.g. "Pricing section needs updating — remove the implementation fee line"`}
            style={{ width: "100%", minHeight: 70, padding: "7px 10px", border: "1px solid #e07b3f", borderRadius: 6, fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn onClick={submitAmend} disabled={loading || !amendNote.trim()} color="#e07b3f">
              {loading ? "Sending…" : "Submit Amendments"}
            </Btn>
            <Btn onClick={() => { setAmendOpen(false); setAmendNote(""); }} outline color="#666">
              Cancel
            </Btn>
          </div>
        </div>
      )}

      {changesOpen && (
        <div style={{ background: "#fff5f5", border: "1px solid #993C1D", borderRadius: 8, padding: 12, marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#993C1D", marginBottom: 6 }}>
            Change request feedback — notifies team only, does not requeue bosses:
          </div>
          <textarea
            value={changesFeedback}
            onChange={e => setChangesFeedback(e.target.value)}
            placeholder="Describe the changes needed..."
            style={{ width: "100%", minHeight: 70, padding: "7px 10px", border: "1px solid #993C1D", borderRadius: 6, fontSize: 13, resize: "vertical", boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn onClick={submitChanges} disabled={loading || !changesFeedback.trim()} color="#993C1D">
              {loading ? "Sending…" : "Request Changes"}
            </Btn>
            <Btn onClick={() => { setChangesOpen(false); setChangesFeedback(""); }} outline color="#666">
              Cancel
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientCommentsSection({ draft, onRefresh }) {
  const [busy, setBusy] = useState(null); // commentId being acted on

  const comments = draft.clientComments || [];

  const migrateCmt = (c) => {
    if (c.thread) return c;
    const thread = [{ from: 'client', text: c.text || '', at: c.createdAt }];
    if (c.teamResponse) thread.push({ from: 'team', text: c.teamResponse, at: c.respondedAt, by: c.respondedBy });
    return { ...c, thread, resolved: !!c.teamResponse };
  };

  const simTeamReply = async (commentId) => {
    setBusy(commentId);
    try {
      await fetch(`/api/draft/${draft.token}/comments/${commentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response: "Thanks for your comment — we will look into this and get back to you shortly." }),
      });
      onRefresh();
    } catch {}
    finally { setBusy(null); }
  };

  const simTeamResolve = async (commentId) => {
    setBusy(commentId);
    try {
      const r = await fetch(`/api/draft/${draft.token}/comments/${commentId}/resolve`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      });
      const d = await r.json().catch(() => ({}));
      onRefresh();
      if (d.allResolved) alert("All comments resolved — status changed to 'sent', client emailed.");
    } catch {}
    finally { setBusy(null); }
  };

  const simResolveAll = async () => {
    setBusy("all");
    try {
      const r = await fetch(`/api/draft/${draft.token}/comments/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!r.ok) { const d = await r.json(); alert(d.error || "Failed"); }
      else { onRefresh(); alert("All resolved & client emailed — status → sent."); }
    } catch {}
    finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <a href={`/comments/${draft.token}`} target="_blank" rel="noreferrer"
          style={{ fontSize: 12, color: "#1a3a6e", fontWeight: 600, textDecoration: "underline" }}>
          Open Comments Review Page ↗
        </a>
        <span style={{ fontSize: 12, color: "#aaa" }}>
          {comments.filter(c => !migrateCmt(c).resolved).length} open · {comments.length} total
        </span>
        {comments.some(c => !migrateCmt(c).resolved) && (
          <Btn onClick={simResolveAll} disabled={busy === "all"} color="#1a6e3c">
            {busy === "all" ? "…" : "Resolve All & Resend →"}
          </Btn>
        )}
      </div>

      {comments.length === 0 && (
        <p style={{ color: "#aaa", fontSize: 13, margin: 0 }}>No comments yet.</p>
      )}

      {comments.map((rawC, idx) => {
        const c = migrateCmt(rawC);
        const thread = c.thread || [];
        return (
          <div key={c.id} style={{
            background: c.resolved ? "#f0fff4" : "#fffbeb",
            border: `1px solid ${c.resolved ? "#c8e6c9" : "#fcd34d"}`,
            borderRadius: 7, padding: "10px 12px", marginBottom: 8,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: c.resolved ? "#2e7d32" : "#b45309" }}>
                Comment {idx + 1} {c.resolved ? "· ✓ Resolved" : "· Open"}
              </span>
              {c.sectionPreview && (
                <span style={{ fontSize: 10, color: "#aaa", fontStyle: "italic", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  "{c.sectionPreview.slice(0, 50)}"
                </span>
              )}
            </div>
            {thread.map((msg, mi) => (
              <div key={mi} style={{ fontSize: 12, marginBottom: 3, color: msg.from === "team" ? "#1565c0" : "#555" }}>
                <b>{msg.from === "team" ? (msg.by || "Team") : "Client"}:</b> {msg.text}
              </div>
            ))}
            {!c.resolved && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <Btn onClick={() => simTeamReply(c.id)} disabled={!!busy} color="#1565C0" outline>
                  {busy === c.id ? "…" : "Sim: Team Reply"}
                </Btn>
                <Btn onClick={() => simTeamResolve(c.id)} disabled={!!busy} color="#1D9E75">
                  {busy === c.id ? "…" : "Sim: Resolve"}
                </Btn>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function JeffCountersignSection({ draft }) {
  if (draft.status === "signed") {
    return (
      <div>
        <div style={{ color: "#1D9E75", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
          ✓ Fully executed — countersigned by Jeff
        </div>
        {draft.jeffSignedAt && (
          <div style={{ fontSize: 12, color: "#888" }}>{new Date(draft.jeffSignedAt).toLocaleString()}</div>
        )}
      </div>
    );
  }

  if (!draft.jeffToken) {
    return <p style={{ color: "#999", fontSize: 13, margin: 0 }}>No Jeff token yet — client must sign first (or jump to "Client Signed" stage).</p>;
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ background: "#f4f5f7", borderRadius: 7, padding: "6px 12px", fontSize: 13, fontFamily: "monospace" }}>
          Jeff Token: <b>{draft.jeffToken}</b> <CopyBtn value={draft.jeffToken} />
        </div>
      </div>
      <Btn onClick={() => window.open(`/countersign/${draft.jeffToken}`, "_blank")}>
        Open Countersign Page ↗
      </Btn>
      <p style={{ fontSize: 12, color: "#888", marginTop: 8, marginBottom: 0 }}>
        Jeff draws his signature here — submitting fully executes the agreement.
      </p>
    </div>
  );
}

function ClientSignSection({ draft }) {
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);

  const token = draft.clientToken;
  const pass  = draft.clientPassword;

  if (draft.status === "signed" || draft.status === "client_signed") {
    return (
      <div>
        <div style={{ color: "#1D9E75", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
          ✓ Signed by {draft.signerName || "client"}
        </div>
        <div style={{ fontSize: 12, color: "#888" }}>
          {draft.signedAt ? new Date(draft.signedAt).toLocaleString() : ""}
        </div>
        {token && (
          <div style={{ marginTop: 8 }}>
            <Btn onClick={() => window.open(`/sign/${token}`, "_blank")} outline color="#888">
              Open Sign Page ↗
            </Btn>
          </div>
        )}
      </div>
    );
  }

  if (!token) {
    return <p style={{ color: "#999", fontSize: 13 }}>Agreement has not been sent to client yet. Complete the Send to Client step first.</p>;
  }

  if (draft.status === "client_commented") {
    return (
      <div>
        <div style={{ fontSize: 12, color: "#b45309", fontWeight: 600, marginBottom: 10 }}>
          Client has left comments — cannot sign until resolved.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn onClick={() => window.open(`/sign/${token}`, "_blank")}>Open Client Sign Page ↗</Btn>
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={{ background: "#f4f5f7", borderRadius: 7, padding: "6px 12px", fontSize: 13, fontFamily: "monospace", display: "inline-flex", gap: 8, alignItems: "center" }}>
            Password: <b>{pass}</b> <CopyBtn value={pass || ""} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ background: "#f4f5f7", borderRadius: 7, padding: "6px 12px", fontSize: 13, fontFamily: "monospace" }}>
          Token: <b>{token}</b> <CopyBtn value={token} />
        </div>
        {pass && (
          <div style={{ background: "#f4f5f7", borderRadius: 7, padding: "6px 12px", fontSize: 13, fontFamily: "monospace" }}>
            Password: <b>{pass}</b> <CopyBtn value={pass} />
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Btn onClick={() => window.open(`/sign/${token}`, "_blank")}>Open Client Sign Page ↗</Btn>
      </div>

      <div style={{ borderTop: "0.5px solid #eee", paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 8 }}>
          Quick test — verify client password:
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Access code"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: 140 }}
          />
          <Btn
            onClick={async () => {
              setLoading(true); setResult(null);
              try {
                const r = await fetch(`/api/sign/${token}`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ action: 'verify', password }),
                });
                const d = await r.json();
                setResult(r.ok ? { ok: true, msg: "✓ Password accepted — sign page will load" } : { ok: false, msg: d.error || "Wrong password" });
              } catch { setResult({ ok: false, msg: "Request failed" }); }
              finally { setLoading(false); }
            }}
            disabled={loading || !password}
            color="#1D9E75"
          >
            {loading ? "Checking…" : "Verify Password"}
          </Btn>
          {result && (
            <span style={{ fontSize: 12, fontWeight: 600, color: result.ok ? "#1D9E75" : "#993C1D" }}>{result.msg}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function SendToClientSection({ draft, onRefresh }) {
  const [email,   setEmail]   = useState(draft.clientEmail || "");
  const [sending, setSending] = useState(false);
  const [done,    setDone]    = useState(!!draft.clientEmail);

  if (draft.status === "pending_review" || draft.status === "signed" || draft.status === "client_signed") return null;

  const send = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`/api/draft/${draft.token}/send-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientEmail: email }),
      });
      if (r.ok) { setDone(true); onRefresh(); }
      else { const d = await r.json(); alert(d.error || "Failed"); }
    } catch { alert("Request failed"); }
    finally { setSending(false); }
  };

  if (draft.status !== "approved" && !done) {
    return <p style={{ color: "#999", fontSize: 13 }}>Waiting for both bosses to approve before sending to client.</p>;
  }

  return (
    <div>
      {done ? (
        <div>
          <p style={{ fontSize: 13, color: "#1D9E75", marginBottom: 8 }}>✓ Sent to <b>{draft.clientEmail}</b></p>
          <Btn onClick={() => setDone(false)} outline color="#666">Send Again</Btn>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="email"
            placeholder="client@company.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, width: 220 }}
          />
          <Btn onClick={send} disabled={sending || !email} color="#1565C0">
            {sending ? "Sending…" : "Send Sign Link →"}
          </Btn>
        </div>
      )}
    </div>
  );
}

function StageControls({ draft, onRefresh }) {
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(null); // which stage is in-flight

  const jump = async (stage) => {
    setLoading(stage);
    try {
      const r = await fetch(`/api/draft/${draft.token}/reset-stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage }),
      });
      if (r.ok) { await onRefresh(); setOpen(false); }
      else { const d = await r.json(); alert(d.error || "Failed"); }
    } catch { alert("Request failed"); }
    finally { setLoading(null); }
  };

  const STAGES = [
    {
      stage: "pending_review",
      label: "← Pending Review",
      desc: "Re-queue for review — clears approvals and client data.",
      color: "#666",
    },
    {
      stage: "approved",
      label: "✓ Approved",
      desc: "Skip boss review — both approvals auto-filled, jump to Send to Client.",
      color: "#1565C0",
    },
    {
      stage: "sent",
      label: "→ Sent to Client",
      desc: "Skip approval + send steps. Fresh client token and access code generated.",
      color: "#1D9E75",
    },
    {
      stage: "client_commented",
      label: "💬 Client Commented",
      desc: "Injects 2 test comments in the new thread format — use to test the comment review flow.",
      color: "#e07b3f",
    },
    {
      stage: "client_signed",
      label: "✍ Client Signed",
      desc: "Simulates the client having signed — generates a jeffToken so you can test Jeff's countersign page.",
      color: "#7c3aed",
    },
  ];

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "1px solid #ddd", borderRadius: 6,
          color: "#888", fontSize: 11, padding: "3px 10px", cursor: "pointer",
        }}
      >
        ⚙ Jump to Stage {open ? "▲" : "▼"}
      </button>

      {open && (
        <div style={{
          marginTop: 6, background: "#fafafa", border: "1px solid #e5e7eb",
          borderRadius: 8, padding: "12px 14px",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
            Reset this agreement to any stage
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {STAGES.map(({ stage, label, desc, color }) => (
              <div key={stage} style={{
                display: "flex", alignItems: "flex-start", gap: 12,
                padding: "8px 10px", background: "#fff", borderRadius: 7,
                border: draft.status === stage ? `1.5px solid ${color}` : "1px solid #eee",
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 11, color: "#888", lineHeight: 1.5 }}>{desc}</div>
                </div>
                <button
                  onClick={() => jump(stage)}
                  disabled={loading !== null}
                  style={{
                    background: color, color: "#fff", border: "none",
                    borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 600,
                    cursor: loading !== null ? "not-allowed" : "pointer",
                    opacity: loading !== null ? 0.6 : 1, whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  {loading === stage ? "…" : "Jump"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AgreementCard({ draft: initialDraft }) {
  const [draft, setDraft] = useState(initialDraft);

  useEffect(() => { setDraft(initialDraft); }, [initialDraft]);

  const refresh = async () => {
    try {
      const r = await fetch(`/api/draft/${draft.token}`);
      const d = await r.json();
      setDraft(d);
    } catch {}
  };

  const isReady = draft.status !== "pending_review";
  const isSent  = ["sent", "client_commented", "client_signed", "signed"].includes(draft.status);
  const hasComments = (draft.clientComments || []).length > 0;
  const isClientSigned = draft.status === "client_signed" || draft.status === "signed";
  const v       = draft.reviewVersion || 1;

  return (
    <div style={{
      background: "#f9f9fb", borderRadius: 12, border: "0.5px solid #e5e7eb",
      marginBottom: 20, overflow: "hidden",
    }}>
      {/* Card header */}
      <div style={{
        padding: "12px 16px", background: "#fff", borderBottom: "0.5px solid #eee",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#1a3a6e" }}>
            {draft.fields?.CUSTOMER_NAME || "Unnamed"}
          </div>
          <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
            {(draft.selectedProductNames || []).join(" · ")}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Badge status={draft.status} />
          {v > 1 && (
            <span style={{
              fontSize: 11, fontWeight: 700, background: "#fff3e0", color: "#e07b3f",
              padding: "2px 8px", borderRadius: 10, border: "1px solid #e07b3f",
            }}>
              v{v}
            </span>
          )}
          <span style={{ fontSize: 11, color: "#bbb" }}>{draft.token}</span>
        </div>
      </div>

      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Step 1: Boss 1 */}
        <Section title="Step 1 — Boss 1 Approval" accent="#1a3a6e">
          <BossActions draft={draft} bossNum={1} onRefresh={refresh} />
        </Section>

        {/* Step 2: Boss 2 */}
        <Section title="Step 2 — Boss 2 Approval" accent="#1a3a6e">
          <BossActions draft={draft} bossNum={2} onRefresh={refresh} />
        </Section>

        {/* Amendment history — only shown when amendments exist */}
        {(draft.amendments || []).length > 0 && (
          <Section title={`Amendment History (${draft.amendments.length})`} accent="#e07b3f">
            <AmendmentHistory draft={draft} />
          </Section>
        )}

        {/* Step 3: Send to client */}
        <Section title="Step 3 — Send to Client" accent={isReady ? "#1565C0" : "#aaa"}>
          <SendToClientSection draft={draft} onRefresh={refresh} />
        </Section>

        {/* Step 4: Client sign page */}
        <Section title="Step 4 — Client Sign Page" accent={isSent ? "#1D9E75" : "#aaa"}>
          <ClientSignSection draft={draft} />
        </Section>

        {/* Step 5: Client comments (shown whenever comments exist or status is client_commented) */}
        {(hasComments || draft.status === "client_commented") && (
          <Section title={`Step 5 — Client Comments (${(draft.clientComments || []).length})`} accent="#e07b3f">
            <ClientCommentsSection draft={draft} onRefresh={refresh} />
          </Section>
        )}

        {/* Step 6: Jeff countersign (shown when client has signed) */}
        {isClientSigned && (
          <Section title="Step 6 — Jeff Counter-Signature" accent={draft.status === "signed" ? "#1D9E75" : "#7c3aed"}>
            <JeffCountersignSection draft={draft} />
          </Section>
        )}

        {/* Test controls */}
        <Section title="Jump to Stage" accent="#888">
          <StageControls draft={draft} onRefresh={refresh} />
        </Section>

      </div>
    </div>
  );
}

export default function TestPanel({ onBack }) {
  const [drafts,     setDrafts]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [clearing,   setClearing]   = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = "";
    };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/drafts");
      const d = await r.json();
      const full = await Promise.all(d.map(async summary => {
        try {
          const r2 = await fetch(`/api/draft/${summary.token}`);
          return await r2.json();
        } catch { return summary; }
      }));
      setDrafts(full);
    } catch { alert("Failed to load drafts"); }
    finally { setLoading(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${drafts.length} agreements? This cannot be undone.`)) return;
    setClearing(true);
    try {
      await Promise.all(drafts.map(d =>
        fetch(`/api/draft/${d.token}`, { method: "DELETE" }).catch(() => {})
      ));
      setDrafts([]);
    } catch { alert("Some deletions failed — try refreshing."); }
    finally { setClearing(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#f4f5f7", fontFamily: "Arial, sans-serif" }}>
      {/* Header */}
      <div style={{
        background: "#1a3a6e", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", padding: "5px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
            ← Home
          </button>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Test Panel</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>Internal use only</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", padding: "5px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
            ↻ Refresh
          </button>
          {drafts.length > 0 && (
            <button
              onClick={clearAll}
              disabled={clearing}
              style={{ background: "rgba(180,0,0,0.25)", border: "1px solid rgba(255,100,100,0.35)", color: "#ffaaaa", padding: "5px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
            >
              {clearing ? "Clearing…" : `🗑 Clear all (${drafts.length})`}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "28px 20px" }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#1a3a6e" }}>Agreement Flow Tester</div>
            <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>Walk through the full agreement flow — including the amendment requeue.</div>
          </div>
          <div style={{ fontSize: 13, color: "#aaa" }}>{drafts.length} agreement{drafts.length !== 1 ? "s" : ""}</div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#aaa" }}>Loading agreements…</div>
        ) : drafts.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, background: "#fff", borderRadius: 12, border: "0.5px solid #eee" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#1a3a6e", marginBottom: 6 }}>No agreements yet</div>
            <div style={{ fontSize: 13, color: "#888" }}>Go back to the builder, select some modules, and submit for approval.</div>
          </div>
        ) : (
          drafts.map(d => <AgreementCard key={d.token} draft={d} />)
        )}
      </div>
    </div>
  );
}
